// Comprime imagens grandes no cliente antes do upload (reduz falhas em redes móveis lentas).
// Para arquivos não-imagem, GIFs ou imagens pequenas, retorna o arquivo original sem alterações.
export async function compressImageIfNeeded(
  file: File,
  maxDimension = 1920,
  quality = 0.75
): Promise<File> {
  if (!file || !file.type) return file;
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  if (file.size < 500 * 1024) return file;
  try {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('read'));
      reader.readAsDataURL(file);
    });
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('decode'));
      i.src = dataUrl;
    });
    let { width, height } = img;
    if (width > maxDimension || height > maxDimension) {
      const scale = maxDimension / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob: Blob | null = await new Promise(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    if (!blob || blob.size >= file.size) return file;
    const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

// Comprime uma lista de arquivos em paralelo.
export async function compressImagesIfNeeded(
  files: File[] | FileList,
  maxDimension = 1920,
  quality = 0.75
): Promise<File[]> {
  const arr = Array.from(files);
  return Promise.all(arr.map(f => compressImageIfNeeded(f, maxDimension, quality)));
}
